import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { postsAPI } from '../api';
import { Plus, Edit, Trash2, Eye, ArrowLeft, CheckSquare, Square, Upload, Download } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const PostsManagement = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all'); // all | draft | scheduled | published
  const [selected, setSelected] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [more, setMore] = useState(false);
  const { toast } = useToast();

  const getAuthHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

  const PER_PAGE = 50;   // the list endpoint will not serve more in one go

  const loadPosts = useCallback(async (nextPage = 0) => {
    setLoading(true);
    try {
      const params = {
        ...(filter !== 'all' && { category: filter }),
        ...(statusFilter !== 'all' && { status: statusFilter }),
        limit: PER_PAGE,
        skip: nextPage * PER_PAGE,
      };
      const response = await axios.get(`${API}/api/posts`, { params, headers: getAuthHeader() });
      setPosts((prev) => (nextPage === 0 ? response.data : [...prev, ...response.data]));
      setMore(response.data.length === PER_PAGE);
      setPage(nextPage);
      setSelected(new Set());
    } catch (error) {
      toast({ title: "Error", description: "Failed to load posts", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [filter, statusFilter, toast]);

  useEffect(() => { loadPosts(0); }, [loadPosts]);

  // Keep the URL in sync so the page is shareable + back-button works
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (statusFilter === 'all') next.delete('status');
    else next.set('status', statusFilter);
    setSearchParams(next, { replace: true });
  }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await postsAPI.delete(id);
      toast({ title: "Deleted", description: "Post deleted" });
      loadPosts(0);
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete", variant: "destructive" });
    }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === posts.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(posts.map(p => p.id)));
    }
  };

  const handleBulkAction = async (action) => {
    if (selected.size === 0) return;
    const label = action === 'delete' ? 'delete' : action === 'publish' ? 'publish' : 'unpublish';
    if (!window.confirm(`${label.charAt(0).toUpperCase() + label.slice(1)} ${selected.size} post(s)?`)) return;
    setBulkLoading(true);
    try {
      await axios.post(`${API}/api/posts/bulk-action`, {
        action,
        post_ids: Array.from(selected),
      }, { headers: getAuthHeader() });
      toast({ title: "Done", description: `${selected.size} post(s) ${label}ed` });
      loadPosts(0);
    } catch (error) {
      toast({ title: "Error", description: `Bulk ${label} failed`, variant: "destructive" });
    } finally {
      setBulkLoading(false);
    }
  };

  const categories = [
    { value: 'all', label: 'All Posts' },
    { value: 'news', label: 'News' },
    { value: 'events', label: 'Events' },
    { value: 'history', label: 'History' },
    { value: 'attractions', label: 'Attractions' },
    { value: 'food-drink', label: 'Food & Drink' },
    { value: 'people', label: 'Famous People' },
    { value: 'announcements', label: 'Announcements' },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard"><Button variant="outline" size="sm"><ArrowLeft size={16} className="mr-2" />Dashboard</Button></Link>
            <h1 className="text-2xl font-bold text-slate-900">Posts Management</h1>
          </div>
          <Link to="/admin/posts/new"><Button className="bg-cyan-500 hover:bg-cyan-600"><Plus size={18} className="mr-2" />New Post</Button></Link>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Status filter pills */}
        <div className="flex gap-2 flex-wrap mb-3" data-testid="status-filters">
          {[
            { value: 'all', label: 'All' },
            { value: 'published', label: 'Published' },
            { value: 'draft', label: 'Drafts' },
            { value: 'scheduled', label: 'Scheduled' },
          ].map((s) => (
            <Button
              key={s.value}
              variant={statusFilter === s.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter(s.value)}
              className={statusFilter === s.value ? 'bg-slate-900 hover:bg-slate-800' : ''}
              data-testid={`status-pill-${s.value}`}
            >
              {s.label}
            </Button>
          ))}
        </div>
        {/* Category filters */}
        <div className="flex gap-2 flex-wrap mb-4">
          {categories.map(cat => (
            <Button
              key={cat.value}
              variant={filter === cat.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(cat.value)}
              className={filter === cat.value ? 'bg-cyan-500 hover:bg-cyan-600' : ''}
            >
              {cat.label}
            </Button>
          ))}
        </div>

        {/* Bulk Actions Bar */}
        {posts.length > 0 && (
          <div className="flex items-center gap-3 mb-4 bg-white rounded-lg px-4 py-3 border border-gray-200" data-testid="bulk-actions-bar">
            <button onClick={toggleSelectAll} className="text-gray-500 hover:text-cyan-600" data-testid="select-all-btn">
              {selected.size === posts.length ? <CheckSquare size={20} className="text-cyan-500" /> : <Square size={20} />}
            </button>
            <span className="text-sm text-gray-500">
              {selected.size > 0 ? `${selected.size} selected` : `${posts.length} posts`}
            </span>
            {selected.size > 0 && (
              <div className="flex items-center gap-2 ml-4">
                <Button size="sm" variant="outline" disabled={bulkLoading} onClick={() => handleBulkAction('publish')} data-testid="bulk-publish-btn">
                  <Upload size={14} className="mr-1.5" /> Publish
                </Button>
                <Button size="sm" variant="outline" disabled={bulkLoading} onClick={() => handleBulkAction('unpublish')} data-testid="bulk-unpublish-btn">
                  <Download size={14} className="mr-1.5" /> Unpublish
                </Button>
                <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" disabled={bulkLoading} onClick={() => handleBulkAction('delete')} data-testid="bulk-delete-btn">
                  <Trash2 size={14} className="mr-1.5" /> Delete
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="max-w-7xl mx-auto px-6 pb-12">
        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-3 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading posts...</p>
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl">
            <p className="text-gray-600 mb-4">No posts found</p>
            <Link to="/admin/posts/new"><Button className="bg-cyan-500 hover:bg-cyan-600"><Plus size={18} className="mr-2" />Create Post</Button></Link>
          </div>
        ) : (
          <div className="space-y-3">
            {posts.map(post => (
              <div key={post.id} className={`bg-white rounded-xl p-4 shadow-sm border transition-all ${selected.has(post.id) ? 'border-cyan-400 bg-cyan-50/30' : 'border-gray-100 hover:shadow-md'}`}>
                <div className="flex gap-4 items-center">
                  <button onClick={() => toggleSelect(post.id)} className="text-gray-400 hover:text-cyan-500 shrink-0" data-testid={`select-${post.id}`}>
                    {selected.has(post.id) ? <CheckSquare size={20} className="text-cyan-500" /> : <Square size={20} />}
                  </button>
                  {post.featured_image && (
                    <img
                      src={post.featured_image}
                      alt=""
                      className="w-32 h-20 object-cover rounded-lg shrink-0"
                      onError={e => { e.target.style.display = 'none'; }}
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-slate-900 truncate">{post.title}</h3>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="text-xs px-2 py-0.5 bg-cyan-100 text-cyan-700 rounded capitalize">{post.category}</span>
                      <span className="text-xs text-gray-400">{new Date(post.created_at).toLocaleDateString()}</span>
                      {post.published ? (
                        <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded">Published</span>
                      ) : post.scheduled_at ? (
                        <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded">Scheduled</span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">Draft</span>
                      )}
                      {post.views > 0 && <span className="text-xs text-gray-400 flex items-center gap-1"><Eye size={11} /> {post.views}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Link to={`/news/${post.slug}`} target="_blank"><Button variant="ghost" size="sm"><Eye size={16} /></Button></Link>
                    <Link to={`/admin/posts/edit/${post.id}`}><Button variant="ghost" size="sm"><Edit size={16} /></Button></Link>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(post.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50"><Trash2 size={16} /></Button>
                  </div>
                </div>
              </div>
            ))}
            {more && (
              <div className="pt-2 text-center">
                <Button
                  variant="outline"
                  onClick={() => loadPosts(page + 1)}
                  disabled={loading}
                  data-testid="posts-load-more-btn"
                >
                  {loading ? 'Loading…' : `Load the next ${PER_PAGE}`}
                </Button>
                <p className="text-xs text-slate-400 mt-2">
                  Showing {posts.length} posts
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PostsManagement;
